/**
 * @fileoverview RecentActivityTabs - Unified timeline feed for recent sessions and projects
 * Displays a vertical timeline with status indicators, filterable by type.
 */
import { Link } from 'react-router-dom'
import { ArrowRight, Archive, FolderKanban, MessageSquare } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { RelativeTime } from '@/components/data-display/RelativeTime'
import { ProjectBadge } from '@/components/projects/ProjectBadge'
import { SessionStatusBadge } from '@/components/sessions/SessionStatusBadge'
import type { SessionStatus } from '@/lib/types'

export interface RecentSession {
  sessionId: string
  goal: string
  status: SessionStatus
  createdAt: string
  projectId?: string
  projectName?: string
}

export interface RecentProject {
  id: string
  name: string
  description?: string
  sessionCount: number
  activeSessionCount: number
  failedSessionCount: number
  createdAt: string
  isArchived?: boolean
}

export interface RecentActivityData {
  sessions: RecentSession[]
  projects: RecentProject[]
}

export interface RecentActivityTabsProps {
  data: RecentActivityData
  isLoading?: boolean
}

function TimelineSkeleton() {
  return (
    <div data-testid="table-skeleton" className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3">
          <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-4 w-16 flex-shrink-0" />
        </div>
      ))}
    </div>
  )
}

function SessionTimeline({ sessions }: { sessions: RecentSession[] }) {
  if (sessions.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No recent sessions. Start a new session to see activity here.</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-border">
      {sessions.map((session) => (
        <Link
          key={session.sessionId}
          to={`/sessions/${session.sessionId}`}
          className="flex items-center gap-3 p-3 hover:bg-accent/50 transition-colors rounded-md -mx-1 px-4 group"
        >
          <div className="flex-shrink-0">
            <SessionStatusBadge status={session.status} size="sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
              {session.goal || truncateId(session.sessionId)}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              {session.projectId && session.projectName ? (
                <ProjectBadge projectId={session.projectId} projectName={session.projectName} size="sm" />
              ) : (
                <span className="text-xs text-muted-foreground">No project</span>
              )}
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground font-mono">{truncateId(session.sessionId)}</span>
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            <RelativeTime date={session.createdAt} className="text-xs text-muted-foreground" />
          </div>
        </Link>
      ))}
    </div>
  )
}

function ProjectTimeline({ projects }: { projects: RecentProject[] }) {
  if (projects.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <FolderKanban className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No projects yet. Create a project to organize your sessions.</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-border">
      {projects.map((project) => (
        <Link
          key={project.id}
          to={`/projects/${project.id}`}
          className="flex items-center gap-3 p-3 hover:bg-accent/50 transition-colors rounded-md -mx-1 px-4 group"
        >
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <FolderKanban className="h-4 w-4" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                {project.name}
              </p>
              {project.isArchived && (
                <Badge variant="outline" className="text-[10px] gap-0.5 px-1.5 py-0">
                  <Archive className="h-2.5 w-2.5" />
                  Archived
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-muted-foreground">
                {project.sessionCount} session{project.sessionCount !== 1 ? 's' : ''}
              </span>
              {project.activeSessionCount > 0 && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                    {project.activeSessionCount} active
                  </span>
                </>
              )}
              {project.description && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                    {project.description}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            <RelativeTime date={project.createdAt} className="text-xs text-muted-foreground" />
          </div>
        </Link>
      ))}
    </div>
  )
}

function truncateId(id: string): string {
  if (id.length <= 20) return id
  return `${id.slice(0, 12)}...${id.slice(-6)}`
}

export function RecentActivityTabs({ data, isLoading }: RecentActivityTabsProps) {
  return (
    <Tabs defaultValue="sessions" className="w-full">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center gap-4">
            <CardTitle className="text-base">Recent Activity</CardTitle>
            <TabsList className="h-8">
              <TabsTrigger value="sessions" className="text-xs px-3">Sessions</TabsTrigger>
              <TabsTrigger value="projects" className="text-xs px-3">Projects</TabsTrigger>
            </TabsList>
          </div>
        </CardHeader>
        <CardContent>
          <TabsContent value="sessions" className="mt-0">
            <div className="flex justify-end mb-1">
              <Button variant="ghost" size="sm" asChild className="text-xs h-7">
                <Link to="/sessions" className="flex items-center gap-1">
                  View All
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
            {isLoading ? <TimelineSkeleton /> : <SessionTimeline sessions={data.sessions} />}
          </TabsContent>
          <TabsContent value="projects" className="mt-0">
            <div className="flex justify-end mb-1">
              <Button variant="ghost" size="sm" asChild className="text-xs h-7">
                <Link to="/projects" className="flex items-center gap-1">
                  View All
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
            {isLoading ? <TimelineSkeleton /> : <ProjectTimeline projects={data.projects} />}
          </TabsContent>
        </CardContent>
      </Card>
    </Tabs>
  )
}
